import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Edit from '@material-ui/icons/Edit';
import Button from '@material-ui/core/Button';
import { useEffect, useState } from 'react';
import SettingsCard from 'components/PhotoAdminComponents/Settings/Card/SettingsCard';
import ActionButton from 'components/common/ActionButton/ActionButton';
import { Form, Formik } from 'formik';
import { useRef } from 'react';
import { CircularProgress } from '@material-ui/core';
import billingAddressChangeSchema from 'validation/UserSettings/BillingAddressChangeSchema';
import { useUserApi } from 'hooks/useUserApi';
import PostalCodeFields from '../../PostalCodeFields/PostalCodeFields';
import styles from './ChangeBillingAddressCard.module.css';
import { useTranslation } from 'react-i18next';

const ChangeBillingAddressCard = ({ user, setInProgress, inProgress, showDialog }) => {
  const [isEditing, setIsEditing] = useState(false);
  const toggleIsEditing = () => setIsEditing(!isEditing);
  const formikRef = useRef(null);
  const { getCurrentUser, putUserAddress } = useUserApi();
  const { t } = useTranslation();

  const [billingAddressData, setBillingAddressData] = useState({
    city: '',
    zipCode: '',
    streetAndNumber: '',
    country: ''
  });

  const putBillingAddressWithDialog = async (body) => {
    setInProgress('billingAddress');
    const status = await putUserAddress(body);
    getCurrentUser();
    showDialog(status);
    setInProgress(null);
  };

  useEffect(() => {
    if (user && formikRef.current) {
      formikRef.current.values.city = billingAddressData?.city;
      formikRef.current.values.country = billingAddressData?.country;
      formikRef.current.values.zipCode = billingAddressData?.zipCode;
      formikRef.current.values.streetAndNumber = billingAddressData?.streetAndNumber;
    }
  }, [billingAddressData, formikRef]);

  useEffect(() => {
    setBillingAddressData({
      city: user?.billingInfo?.billingAddress?.city || '',
      country: user?.billingInfo?.billingAddress?.country || 'Magyarország',
      zipCode: user?.billingInfo?.billingAddress?.zipCode || '',
      streetAndNumber: user?.billingInfo?.billingAddress?.streetAndNumber || ''
    });
  }, [user]);

  return (
    <SettingsCard
      title={t('billingForm.billingAddress')}
      actionButton={
        <ActionButton
          icon={<Edit style={{ color: 'black' }} />}
          clickHandler={toggleIsEditing}
          className={styles.actionButtonStyle}
        />
      }>
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={{
          city: user?.billingInfo?.billingAddress?.city || '',
          country: user?.billingInfo?.billingAddress?.country || 'Magyarország',
          zipCode: user?.billingInfo?.billingAddress?.zipCode || '',
          streetAndNumber: user?.billingInfo?.billingAddress?.streetAndNumber || ''
        }}
        validationSchema={billingAddressChangeSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          setSubmitting(true);
          await putBillingAddressWithDialog({
            ...user,
            billingInfo: {
              ...user.billingInfo,
              billingAddress: {
                ...user.billingInfo.billingAddress,
                city: values.city,
                zipCode: values.zipCode,
                country: values.country,
                streetAndNumber: values.streetAndNumber
              }
            }
          });
          setIsEditing(false);
          setSubmitting(false);
        }}>
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, resetForm }) => (
          <Form className={styles.form}>
            <Grid container spacing={2}>
              <PostalCodeFields
                data={billingAddressData}
                setData={setBillingAddressData}
                disabled={!isEditing}
                zipHelper={formikRef?.current?.touched?.zipCode}
                zipError={formikRef?.current?.errors?.zipCode}
                cityHelper={formikRef?.current?.touched?.city}
                citypError={formikRef?.current?.errors?.city}
              />

              <Grid item xs={12} md={6}>
                <TextField
                  disabled={!isEditing}
                  variant='outlined'
                  fullWidth
                  required
                  label={t('billingForm.streetAndNumber')}
                  id='streetAndNumber'
                  name='streetAndNumber'
                  type='text'
                  value={values?.streetAndNumber}
                  onChange={handleChange}
                  error={touched.streetAndNumber && Boolean(errors.streetAndNumber)}
                  helperText={touched.streetAndNumber && errors.streetAndNumber}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  disabled
                  variant='outlined'
                  fullWidth
                  required
                  label={t('billingForm.country')}
                  id='country'
                  name='country'
                  type='text'
                  value={values.country}
                  onChange={handleChange}
                  error={touched.country && Boolean(errors.country)}
                  helperText={touched.country && errors.country}
                />
              </Grid>
            </Grid>
            <Grid container className={styles.gridContainer}>
              <Grid item xs={12}>
                <Button disabled={!isEditing} type='submit' fullWidth onClick={handleSubmit} variant='outlined'>
                  {inProgress === 'billingAddress' ? (
                    <>
                      {t('common.inProgress')}
                      <CircularProgress style={{ marginLeft: '0.5rem' }} size={24} />
                    </>
                  ) : (
                    t('profile.billing.updateAddress')
                  )}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </SettingsCard>
  );
};

export default ChangeBillingAddressCard;
